import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Filter } from '../../../../../models/filter';
import { statusFilter } from '../../../manage-users/core/manage-users.selectors';
import { debounceTime, Subject, takeUntil } from 'rxjs';
import { Store } from '@ngrx/store';
import * as fromRoot from '../../../../../state/app.state';

import {
  ClearParticipantFilterRequest,
  FetchGroupFilterRequest,
  FetchParticipantFilterFailure,
  FetchParticipantFilterRequest,
  GenerateAttendanceReportRequest,
} from '../../core/coach-groups-list.actions';
import { FetchStatusFilterRequest } from '../../../clients-list/core/clients-list.actions';
import {
  groupFilter,
  participantFilter,
} from '../../core/coach-groups-list.selectors';

@Component({
  selector: 'app-download-attendance-report-modal',
  templateUrl: './download-attendance-report-modal.component.html',
  styleUrls: ['./download-attendance-report-modal.component.css'],
})
export class DownloadAttendanceReportModalComponent
  implements OnInit, OnDestroy
{
  form = new FormGroup({
    groupId: new FormControl([], Validators.required),
    searchGroup: new FormControl(null),
    participantId: new FormControl([]),
    searchParticipant: new FormControl(null),
    fromDate: new FormControl(null, [Validators.required]),
    toDate: new FormControl(null, [Validators.required]),
  });

  errorMessageMap = {
    groupId: {
      required: 'This field is required.',
    },
    fromDate: {
      required: 'This field is required.',
    },
    toDate: {
      required: 'This field is required.',
    },
  };
  errorMessages: { [key: string]: string } = {};

  participantStatusFilterList: Filter;
  groupFilterList: any;
  participantFilterList: any;

  private readonly OnDestroy: Subject<any> = new Subject<any>();

  constructor(private store: Store<fromRoot.State>) {
    this.store.dispatch(FetchStatusFilterRequest());
    this.store.dispatch(
      FetchGroupFilterRequest({ payload: { pageNumber: 1 } }),
    );
  }

  ngOnInit() {
    this.form.controls['searchGroup'].valueChanges
      .pipe(debounceTime(1000), takeUntil(this.OnDestroy))
      .subscribe((search) => {
        this.store.dispatch(
          FetchGroupFilterRequest({
            payload: {
              pageNumber: 1,
              shouldInclude: this.form.value.groupId,
              ...(search ? { search } : {}),
            },
          }),
        );
      });

    this.form.controls['groupId'].valueChanges
      .pipe(debounceTime(1000), takeUntil(this.OnDestroy))
      .subscribe((search) => {
        this.checkForErrors('groupId');
        this.store.dispatch(ClearParticipantFilterRequest());
        if (this.form.controls['groupId']?.value?.length) {
          this.store.dispatch(
            FetchParticipantFilterRequest({
              payload: {
                pageNumber: 1,
                shouldInclude: this.form.value.groupId,
                groupId: this.form.value.groupId,
              },
            }),
          );
        }
      });

    this.form.controls['searchParticipant'].valueChanges
      .pipe(debounceTime(1000), takeUntil(this.OnDestroy))
      .subscribe((search) => {
        this.store.dispatch(
          FetchParticipantFilterRequest({
            payload: {
              pageNumber: 1,
              shouldInclude: this.form.value.groupId,
              ...(search ? { search } : {}),
            },
          }),
        );
      });

    this.form.controls['fromDate'].valueChanges
      .pipe(debounceTime(300), takeUntil(this.OnDestroy))
      .subscribe((value) => {
        if (value) {
          // Reset toDate when fromDate changes
          this.form.get('toDate').setValue(null, { emitEvent: false });
        }
        this.checkForErrors('fromDate');
      });

    this.form.controls['toDate'].valueChanges
      .pipe(debounceTime(300), takeUntil(this.OnDestroy))
      .subscribe((value) => {
        this.checkForErrors('toDate');
      });

    this.store
      .select(groupFilter)
      .pipe(takeUntil(this.OnDestroy))
      .subscribe((data) => {
        this.groupFilterList = data;
      });

    this.store
      .select(participantFilter)
      .pipe(takeUntil(this.OnDestroy))
      .subscribe((data) => {
        this.participantFilterList = data;
      });

    this.store
      .select(statusFilter)
      .pipe(takeUntil(this.OnDestroy))
      .subscribe((data) => {
        this.participantStatusFilterList = data;
      });
  }

  onDateChange(fieldName: string) {
    this.checkForErrors(fieldName);

    // If fromDate changes, reset toDate validation messages
    if (fieldName === 'fromDate' && this.errorMessages['toDate']) {
      const { toDate, ...rest } = this.errorMessages;
      this.errorMessages = rest;
    }
  }

  checkForErrors(fieldName: string) {
    const control = this.form.get(fieldName);
    if (control && control.invalid && (control.dirty || control.touched)) {
      const errors: { [key: string]: string } = {};
      Object.keys(this.errorMessageMap[fieldName] || {}).forEach((errorKey) => {
        if (control.hasError(errorKey)) {
          errors[fieldName] = this.errorMessageMap[fieldName][errorKey];
        }
      });
      this.errorMessages = { ...this.errorMessages, ...errors };
    } else {
      const { [fieldName]: removed, ...rest } = this.errorMessages;
      this.errorMessages = rest;
    }
  }

  groupsOnNextPage() {
    const search = this.form.controls['searchGroup']?.value || '';
    this.store.dispatch(
      FetchGroupFilterRequest({
        payload: {
          pageNumber: this.groupFilterList.pageNumber + 1,
          shouldInclude: this.form.value.groupId,
          ...(search ? { search } : {}),
        },
      }),
    );
  }

  participantsOnNextPage() {
    const search = this.form.controls['searchGroup']?.value || '';
    this.store.dispatch(
      FetchGroupFilterRequest({
        payload: {
          pageNumber: this.groupFilterList.pageNumber + 1,
          shouldInclude: this.form.value.groupId,
          ...(search ? { search } : {}),
        },
      }),
    );
  }

  handleSubmit() {
    // Mark all fields as touched to ensure validation shows
    Object.keys(this.form.controls).forEach((key) => {
      this.form.get(key).markAsTouched();
      this.checkForErrors(key);
    });

    // Get required field values
    const groupId = this.form.get('groupId').value;
    const fromDate = this.form.get('fromDate').value;
    const toDate = this.form.get('toDate').value;

    // Directly check if required fields are valid
    if (!groupId || groupId.length === 0 || !fromDate || !toDate) {
      return;
    }

    const { searchGroup, searchParticipant, ...payload } =
      this.form.getRawValue();

    // convert dates to ISO format
    if (payload.fromDate) {
      payload.fromDate = new Date(payload.fromDate).toISOString();
    }
    if (payload.toDate) {
      payload.toDate = new Date(payload.toDate).toISOString();
    }

    // remove all null values
    Object.keys(payload).forEach((key) => {
      if (payload[key] === null) {
        delete payload[key];
      }
    });

    this.store.dispatch(GenerateAttendanceReportRequest({ payload }));
  }

  ngOnDestroy() {
    this.OnDestroy.next(null);
    this.OnDestroy.complete();
  }
}
